// ─────────────────────────────────────────────────────────────────────────────
// GrievanceLogManager — operational Grievance Log area.
//   list   → table of all grievances in scope + "New Grievance" button
//   detail → full GrievanceDetail for a clicked grievance
//   create → 4-step GrievanceCreateForm
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState } from "react";
import { Card, Pill, NotifyMark, val, fmtDate, EmptyState, F585Banner } from "./reports/_shared";
import { CATEGORIES, STATUSES, categoryLabel, departmentLabel, statusLabel, daysToClose } from "./grievanceModel";
import GrievanceDetail from "./GrievanceDetail";
import GrievanceCreateForm from "./GrievanceCreateForm";

export default function GrievanceLogManager({ records = [], facilityCcn, facilityName, facilities, source, filedBy, onCreate }) {
  const [mode, setMode] = useState("list"); // list | detail | create
  const [selected, setSelected] = useState(null);

  const sorted = [...records].sort((a, b) => String(b.dateReceived || "").localeCompare(String(a.dateReceived || "")));

  if (mode === "create") {
    return (
      <GrievanceCreateForm
        defaultFacilityCcn={facilityCcn}
        facilities={facilities}
        source={source}
        filedBy={filedBy}
        onCancel={() => setMode("list")}
        onSave={(record) => { onCreate && onCreate(record); setSelected(record); setMode("detail"); }}
      />
    );
  }

  if (mode === "detail" && selected) {
    return (
      <GrievanceDetail
        record={selected}
        facilityName={facilityName}
        // Who is exporting, which is a different fact from who authored the
        // record — the footer says both, so a copy run off by an administrator
        // does not read as one the administrator wrote.
        generatedBy={filedBy}
        onBack={() => { setSelected(null); setMode("list"); }}
      />
    );
  }

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto" }}>
      {/* Above the log rather than inside it: the rule is why the log exists,
          and the columns below are how it is satisfied. */}
      <F585Banner />
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: "var(--ed-ink)", margin: 0 }}>Grievance Log</h2>
          <div style={{ fontSize: 13, color: "var(--ed-muted)", marginTop: 2 }}>{sorted.length} grievance{sorted.length === 1 ? "" : "s"} · {facilityName || "All Facilities"}</div>
        </div>
        <button className="btn-primary" onClick={() => setMode("create")}>＋ New Grievance</button>
      </div>

      {sorted.length === 0 ? (
        <EmptyState>No grievances in this scope yet. Click “New Grievance” to log one.</EmptyState>
      ) : (
        <Card style={{ padding: 0, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "var(--ed-bg)", borderBottom: "1px solid var(--ed-line)" }}>
                {["Received", "Resident", "Category", "Dept", "Status", "Days", "Notice", ""].map((h) => (
                  <th key={h} style={th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((r) => {
                const d = daysToClose(r);
                const st = STATUSES[r.status] || {};
                return (
                  <tr key={r.id} className="table-row" onClick={() => { setSelected(r); setMode("detail"); }} style={{ borderBottom: "1px solid var(--ed-line-2)", cursor: "pointer" }}>
                    <td style={td}>{val(fmtDate(r.dateReceived))}</td>
                    <td style={td}>
                      {val(r.residentName, { missing: r.complainantType !== "anonymous" })}
                      {r.createdLocally && <span title="Locally created" style={{ marginLeft: 6, color: "var(--ed-violet-deep)" }}>•</span>}
                    </td>
                    <td style={td}><Pill label={categoryLabel(r.category)} color={CATEGORIES[r.category]?.color} bg={CATEGORIES[r.category]?.bg} /></td>
                    <td style={td}>{departmentLabel(r.department)}</td>
                    <td style={td}><Pill label={statusLabel(r.status)} color={st.color} bg={st.bg} border={st.border} /></td>
                    <td style={{ ...td, fontWeight: 700, color: d == null ? "var(--ed-muted-2)" : d > 5 ? "var(--ed-alert)" : "var(--ed-ink)" }}>{d == null ? "—" : d}</td>
                    <td style={{ ...td, textAlign: "center" }}><NotifyMark sent={r.residentNotificationSent} /></td>
                    <td style={{ ...td, color: "var(--ed-muted-2)" }}>›</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

const th = { textAlign: "left", padding: "10px 12px", fontSize: 11, fontWeight: 700, color: "var(--ed-muted)", textTransform: "uppercase", letterSpacing: ".04em", whiteSpace: "nowrap" };
const td = { padding: "10px 12px", whiteSpace: "nowrap" };
